


























































import { differenceInHours, parse } from 'date-fns';
import Vue from 'vue';
import Component from 'vue-class-component';
import {
	mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import NpSelect from '@/components/NpSelect.vue';
import Customer from '@/models/Customer';
import Quote from '@/models/Quote';
import Reservation from '@/models/Reservation';
import {
	debounce, getOrString, getOrTrue, getOrZero,
} from '@/utilities/helpers';

interface FppOptions {
	label: string;
	points: number;
	value: number;
}

const Setup = Vue.extend({
	name: 'ReservationFppSelector',
	components: { NpSelect },
	props: {
		value: Number,
	},
	computed: {
		...mapGetters(['customer']),
		...mapState('reservation', ['originalReservation', 'reservation']),
		...mapGetters('reservation', ['quote']),
	},
	watch: {
		model(value: number, newValue: number) {
			if (value !== newValue) {
				this.updateFpp();
			}
		},
	},
	methods: {
		...mapMutations('reservation', ['addFpp']),
		...mapActions('reservation', ['getQuotes']),

		updateFpp() {
			// override
		},
	},
});

@Component
export default class ReservationFppSelector extends Setup {
	public isLoading: boolean = false;
	public updateMessage: string = '';

	public updateFpp = debounce(this.updateQuote, 500, { leading: false, trailing: true });

	// State
	public customer!: Customer;
	public originalReservation!: Reservation;
	public reservation!: Reservation;

	// Getters
	public quote!: Quote;

	// Mutations
	public addFpp!: (points: number) => void;

	// Actions
	public getQuotes!: (payload?: any) => Promise<Quote[]>;

	public get availableFppDays(): number {
		let calendar_day = 0;
		if (!this.fppPerDay || !this.customerFpp) {
			return 0;
		}

		if (this.reservation.quote && this.reservation.quote.details && this.reservation.quote.details.calendar_day) {
			calendar_day = this.reservation.quote.details.days;
			const days = Math.floor(this.customerFpp / this.fppPerDay);
			return days > calendar_day ? calendar_day : days;
		}

		if (this.reservation.quote && this.reservation.quote.is_event) {
			const event_length = Math.ceil(differenceInHours(this.reservation.quote.event_dates[1], this.reservation.quote.event_dates[0]) / 24);
			const days = Math.floor(this.customerFpp / this.fppPerDay);
			return days > event_length ? event_length : days;
		}

		const length = Math.ceil(differenceInHours(
			parse(getOrString('end_date', this.reservation)),
			parse(getOrString('start_date', this.reservation)),
		) / 24);

		if (!length) {
			return 1;
		}

		const days = Math.floor(this.customerFpp / this.fppPerDay);
		return days > length ? length : days;
	}

	public get availableFppDollars(): number {
		if (!this.fppPerDollar || !this.customerFpp) {
			return 0;
		}

		const totalDollars = Math.ceil(this.quote.parking_rate_subtotal);
		const availableDollars = Math.floor(this.customerFpp / this.fppPerDollar);

		return availableDollars > totalDollars ? totalDollars : availableDollars;
	}

	public get canUseFpp(): boolean {
		return !this.customer.no_fpp_earnings
			&& !this.settings.disable_fpp
			&& this.quoteAllowsFpp
			&& (this.availableFppDays > 0 || this.availableFppDollars > 0);
	}

	public get customerFpp(): number {
		return getOrZero('customer.fpp', this) + this.originalReservation.fpp_redeemed;
	}

	public get dollarsModel(): number {
		if (!this.fppPerDollar) {
			return 0;
		}

		return this.reservation.fpp_redeemed / this.fppPerDollar;
	}

	public set dollarsModel(value: number) {
		if (!this.fppPerDollar) {
			return;
		}

		if (value < 0) {
			value = 0;
		}

		if (value > this.availableFppDollars) {
			value = this.availableFppDollars;
		}

		this.model = value * this.fppPerDollar;
	}

	public get fppPerDay(): number {
		return getOrZero('details.fpp_per_day', this.quote);
	}

	public get fppPerDollar(): number {
		return getOrZero('details.fpp_per_dollar', this.quote);
	}

	public get fppOptions(): FppOptions[] {
		const maxDays = this.availableFppDays;

		return Array((maxDays + 1)).fill(0).map((value, index) => {
			let label = '';

			switch (index) {
				case 0:
					label = 'Do not use points';
					break;
				case 1:
					label = '1 day';
					break;
				default:
					label = `${index} days`;
					break;
			}

			return { label, value: index, points: this.fppPerDay * index };
		});
	}

	public get model(): number {
		return this.reservation.fpp_redeemed || 0;
	}

	public set model(value: number) {
		this.addFpp(value);
	}

	public get quoteAllowsFpp(): boolean {
		const quoteDoesNotAllowFpp = getOrTrue('details.no_fpp', this.quote);

		return !quoteDoesNotAllowFpp;
	}

	public get settings(): Locations.Settings {
		return this.$store.getters['location/settings'];
	}

	public created() {
		if (!this.originalReservation) {
			return;
		}

		let fpp_redeemed = this.originalReservation.fpp_redeemed;
		if (this.availableFppDays > 0
			&& this.availableFppDays < Math.floor(this.originalReservation.fpp_redeemed / this.fppPerDay)) {
			fpp_redeemed = this.availableFppDays * this.fppPerDay;
		} else if (this.availableFppDollars > 0
			&& this.availableFppDollars < Math.floor(this.originalReservation.fpp_redeemed / this.fppPerDollar)) {
			fpp_redeemed = this.availableFppDollars * this.fppPerDollar;
		}
		this.model = fpp_redeemed;
	}

	public async updateQuote() {
		this.isLoading = true;
		this.updateMessage = '';

		try {
			await this.getQuotes();
			this.updateMessage = 'Quote has been updated.';
			this.addFpp(this.model);
			setTimeout(() => {
				this.updateMessage = '';
			}, 3000);
		} catch (errors) {
			// Error getting quote.
		}

		this.isLoading = false;
	}
}
